import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import type { FileUpload as FileUploadType } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { downloadTemplate, getTemplateBytes, getTemplateRules, saveTemplateRules } from '../api';
import type { TemplateRule } from '../api';

type PreviewData = { filename: string; html: string };

interface Props {
  templates: string[];
  columnHeaders: string[];
  onUpload: (files: File[]) => Promise<void>;
  onDelete: (filename: string) => Promise<void>;
  locked?: boolean;
  refreshRulesKey?: number;
}

function fileExtension(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function FileTypeBadge({ name }: { name: string }) {
  const ext = fileExtension(name);
  if (ext === 'docx') return <span className="file-type-badge file-type-badge--docx">docx</span>;
  if (ext === 'xlsx') return <span className="file-type-badge file-type-badge--xlsx">xlsx</span>;
  return <span className="file-type-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>{ext}</span>;
}

const emptyRule = (): TemplateRule => ({ folder: '', filenameTemplate: '' });

export function TemplatesPage({ templates, columnHeaders, onUpload, onDelete, locked = false, refreshRulesKey = 0 }: Props) {
  const uploadRef = useRef<FileUploadType | null>(null);
  const [rules, setRules] = useState<Record<string, TemplateRule>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);

  const openPreview = async (filename: string) => {
    setPreviewLoading(filename);
    try {
      const buffer = await getTemplateBytes(filename);
      let html = '';
      if (filename.toLowerCase().endsWith('.docx')) {
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
        html = result.value;
      } else {
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        html = XLSX.utils.sheet_to_html(sheet);
      }
      setPreview({ filename, html: DOMPurify.sanitize(html) });
    } finally {
      setPreviewLoading(null);
    }
  };

  useEffect(() => {
    getTemplateRules().then(setRules).catch(() => {});
  }, [refreshRulesKey]);

  const handleUpload = async (event: { files: File[] }) => {
    await onUpload(event.files);
    uploadRef.current?.clear();
  };

  const updateRule = (filename: string, field: keyof TemplateRule, value: string) => {
    setRules(prev => ({
      ...prev,
      [filename]: { ...(prev[filename] ?? emptyRule()), [field]: value },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip rules for templates that no longer exist and persist
      const active = Object.fromEntries(
        Object.entries(rules).filter(([name]) => templates.includes(name))
      );
      await saveTemplateRules(active);
      setRules(active);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (filename: string, field: keyof TemplateRule, key: string) => {
    const current = rules[filename]?.[field] ?? '';
    updateRule(filename, field, current + `{${key}}`);
  };

  return (
    <>
      {/* ── Template preview dialog ── */}
      <Dialog
        visible={preview !== null}
        onHide={() => setPreview(null)}
        header={
          preview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <FileTypeBadge name={preview.filename} />
              <span>{preview.filename}</span>
            </div>
          )
        }
        style={{ width: '70vw', maxWidth: '1000px' }}
        modal
        dismissableMask
      >
        {preview && (
          <div
            style={{ maxHeight: '65vh', overflowY: 'auto', fontSize: '0.88rem', lineHeight: 1.6 }}
            className="template-preview-content"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        )}
      </Dialog>

      {/* ── Admin lock banner ── */}
      {locked && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          marginBottom: '0.5rem',
          fontSize: '0.88rem',
          color: '#92400e',
        }}>
          <i className="pi pi-lock" style={{ flexShrink: 0 }} />
          <span>An admin is currently editing your template configuration. Changes will appear automatically.</span>
        </div>
      )}

      {/* ── Template list ── */}
      <Card className="section-card">
        <div className="section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <h3 className="m-0">Templates</h3>
            {templates.length > 0 && (
              <span className="count-badge">{templates.length}</span>
            )}
          </div>
          <FileUpload
            ref={uploadRef}
            mode="basic"
            name="files"
            chooseLabel="Upload"
            chooseOptions={{ icon: 'pi pi-upload' }}
            multiple
            auto
            customUpload
            uploadHandler={handleUpload}
            accept=".docx,.xlsx"
            disabled={locked}
          />
        </div>

        {templates.length === 0 ? (
          <div className="template-empty">
            <i className="pi pi-inbox" />
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>No templates yet</div>
            <div style={{ fontSize: '0.82rem' }}>Upload .docx or .xlsx files using the button above.</div>
          </div>
        ) : (
          <div>
            {templates.map(name => (
              <div key={name} className="template-row">
                <div className="template-name">
                  <FileTypeBadge name={name} />
                  <span>{name}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <Button
                    icon={previewLoading === name ? 'pi pi-spin pi-spinner' : 'pi pi-eye'}
                    text
                    rounded
                    size="small"
                    onClick={() => openPreview(name)}
                    tooltip="Preview template"
                    tooltipOptions={{ position: 'left' }}
                    disabled={previewLoading === name}
                  />
                  <Button
                    icon="pi pi-download"
                    text
                    rounded
                    size="small"
                    onClick={() => downloadTemplate(name)}
                    tooltip="Download template"
                    tooltipOptions={{ position: 'left' }}
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    rounded
                    size="small"
                    onClick={() => onDelete(name)}
                    tooltip="Delete template"
                    tooltipOptions={{ position: 'left' }}
                    disabled={locked}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Output rules ── */}
      {templates.length > 0 && (
        <Card className="section-card">
          <div className="section-header">
            <div>
              <h3 className="m-0">Output Rules</h3>
              <p className="m-0 mt-1" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Define the folder and filename for each template inside the generated zip.
                Use <code style={{ background: '#f1f5f9', padding: '0 0.3rem', borderRadius: '0.2rem' }}>{'{COLUMN_NAME}'}</code> to insert data values.
              </p>
            </div>
            <Button
              label={saving ? 'Saving…' : 'Save rules'}
              icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
              disabled={!dirty || saving || locked}
              onClick={handleSave}
            />
          </div>

          {/* Column hint chips */}
          {columnHeaders.length > 0 && (
            <div className="rule-hint-row">
              <span className="rule-hint-label">Available columns:</span>
              {columnHeaders.map(col => (
                <span key={col} className="rule-hint-chip" title={col}>
                  {'{' + col + '}'}
                </span>
              ))}
            </div>
          )}
          {columnHeaders.length === 0 && (
            <div className="rule-hint-row" style={{ color: '#94a3b8', fontStyle: 'italic' }}>
              Upload a data index on the Generate page to see available column placeholders.
            </div>
          )}

          <div className="rules-table">
            {/* Header */}
            <div className="rules-table-header">
              <span>Template</span>
              <span>Folder in zip</span>
              <span>Filename pattern</span>
            </div>

            {templates.map(name => {
              const rule = rules[name] ?? emptyRule();
              return (
                <div key={name} className="rules-table-row">
                  <div className="rules-table-name">
                    <FileTypeBadge name={name} />
                    <span style={{ fontSize: '0.85rem' }}>{name}</span>
                  </div>

                  <div className="rules-field-group">
                    <InputText
                      value={rule.folder}
                      onChange={e => updateRule(name, 'folder', e.target.value)}
                      placeholder="e.g. contracts/{YEAR}"
                      style={{ width: '100%', fontSize: '0.85rem' }}
                    />
                    {columnHeaders.length > 0 && (
                      <div className="rules-chip-row">
                        {columnHeaders.map(col => (
                          <button
                            key={col}
                            className="rules-insert-chip"
                            onClick={() => insertPlaceholder(name, 'folder', col)}
                            title={`Insert {${col}}`}
                          >
                            {col}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rules-field-group">
                    <InputText
                      value={rule.filenameTemplate}
                      onChange={e => updateRule(name, 'filenameTemplate', e.target.value)}
                      placeholder={`e.g. {ID}_${name}`}
                      style={{ width: '100%', fontSize: '0.85rem' }}
                    />
                    {columnHeaders.length > 0 && (
                      <div className="rules-chip-row">
                        {columnHeaders.map(col => (
                          <button
                            key={col}
                            className="rules-insert-chip"
                            onClick={() => insertPlaceholder(name, 'filenameTemplate', col)}
                            title={`Insert {${col}}`}
                          >
                            {col}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="m-0 mt-3" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
            Leave a field empty to use the default: folder is the zip root, filename is <code>lookupKey_templateName</code>.
          </p>
        </Card>
      )}
    </>
  );
}
